import {
  BarChart3,
  Briefcase,
  Building2,
  ClipboardCheck,
  Megaphone,
  Settings,
  ShoppingBag,
  Smartphone,
  Wallet,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { UserRole, type AuthUser } from '@/types';
import { PAINEL_OFICINA_BASE } from '@/lib/painelOficina';
import { DIRETORIA_MODULE_TITLE } from '@/data/diretoriaConstants';
import { RiqueIcon } from '@/components/Rique/RiqueIcon';

export type PortalModuleStatus = 'available' | 'soon';
export type PortalModuleGroup = 'operation' | 'growth' | 'management';

export interface PortalModuleShortcut {
  label: string;
  href?: string;
  openAction?: 'rique';
}

export interface PortalModule {
  id: string;
  title: string;
  description: string;
  href?: string;
  openAction?: 'rique';
  icon: LucideIcon | typeof RiqueIcon;
  group: PortalModuleGroup;
  status: PortalModuleStatus;
  roles: UserRole[];
  requiredModules?: string[];
  shortcuts?: PortalModuleShortcut[];
}

export const PORTAL_MODULE_GROUP_LABELS: Record<PortalModuleGroup, string> = {
  operation: 'Operação',
  growth: 'Crescimento',
  management: 'Gestão e administração',
};

export const PORTAL_MODULES: PortalModule[] = [
  {
    id: 'cobrancas',
    title: 'Cobranças',
    description: 'Dashboard de cobranças, kanbans, títulos, gestor de cobranças e relatórios operacionais.',
    href: '/dashboard',
    icon: ClipboardCheck,
    group: 'operation',
    status: 'available',
    roles: [UserRole.ADMIN, UserRole.GESTOR, UserRole.OPERADOR],
    requiredModules: ['/dashboard', '/kanban', '/gestor-cobrancas', '/titulos', '/relatorios', '/whatsapp-cobrancas-report'],
    shortcuts: [
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'Kanban', href: '/kanban' },
      { label: 'Gestor', href: '/gestor-cobrancas' },
      { label: 'Títulos', href: '/titulos' },
      { label: 'Meus clientes', href: '/clientes-fornecedores' },
    ],
  },
  {
    id: 'aplicativo',
    title: 'Aplicativo',
    description: 'Configurações do app Zeh Motoca, notificações push e recursos para locatários.',
    href: '/configuracoes/aplicativo-zehmotoca',
    icon: Smartphone,
    group: 'operation',
    status: 'available',
    roles: [UserRole.ADMIN, UserRole.GESTOR],
    requiredModules: ['/configuracoes/aplicativo-zehmotoca', '/push-notifications'],
    shortcuts: [
      { label: 'Configurar app', href: '/configuracoes/aplicativo-zehmotoca' },
      { label: 'Push', href: '/push-notifications' },
    ],
  },
  {
    id: 'oficina',
    title: 'Oficina',
    description: 'Painel da oficina para acompanhamento visual da operação.',
    href: PAINEL_OFICINA_BASE,
    icon: Wrench,
    group: 'operation',
    status: 'available',
    roles: [UserRole.ADMIN, UserRole.GESTOR],
    requiredModules: [PAINEL_OFICINA_BASE],
    shortcuts: [{ label: 'Painel oficina', href: PAINEL_OFICINA_BASE }],
  },
  {
    id: 'locacoes',
    title: 'Locações',
    description: 'Gestão de contratos, disponibilidade, renovações e visão financeira de locações.',
    icon: Building2,
    group: 'operation',
    status: 'soon',
    roles: [UserRole.ADMIN, UserRole.GESTOR],
  },
  {
    id: 'e-ai-zeh',
    title: 'Rique.Ia',
    description:
      'Assistente analista do ecossistema PortalFly e Zeh Motoca — chat em linguagem natural com dados de cobrança e ERP.',
    openAction: 'rique',
    icon: RiqueIcon,
    group: 'growth',
    status: 'available',
    roles: [UserRole.ADMIN, UserRole.GESTOR],
    shortcuts: [{ label: 'Abrir chat', openAction: 'rique' }],
  },
  {
    id: 'marketing',
    title: 'Marketing',
    description: 'Campanhas, templates, automações de e-mail, push e comunicação com clientes.',
    icon: Megaphone,
    group: 'growth',
    status: 'soon',
    roles: [UserRole.ADMIN, UserRole.GESTOR],
    requiredModules: ['/email-automation', '/email-personalization', '/email-analytics'],
  },
  {
    id: 'comercial',
    title: 'Comercial',
    description: 'Funil comercial, leads, carteira e oportunidades de venda.',
    icon: Briefcase,
    group: 'growth',
    status: 'soon',
    roles: [UserRole.ADMIN, UserRole.GESTOR],
  },
  {
    id: 'diretoria',
    title: DIRETORIA_MODULE_TITLE,
    description: 'Indicadores executivos, visão consolidada e acompanhamento estratégico.',
    href: '/diretoria',
    icon: BarChart3,
    group: 'management',
    status: 'available',
    roles: [UserRole.ADMIN, UserRole.GESTOR],
    requiredModules: ['/diretoria'],
  },
  {
    id: 'financeiro',
    title: 'Financeiro',
    description: 'Módulo em planejamento: visão financeira, fluxo de caixa e integrações. A abertura e o escopo serão definidos em breve.',
    icon: Wallet,
    group: 'management',
    status: 'soon',
    roles: [UserRole.ADMIN, UserRole.GESTOR],
  },
  {
    id: 'mercado-zeh',
    title: 'Mercado do Zeh',
    description: 'Produtos, ofertas e iniciativas comerciais ligadas ao Mercado do Zeh.',
    href: '/mercado-zeh',
    icon: ShoppingBag,
    group: 'growth',
    status: 'available',
    roles: [UserRole.ADMIN, UserRole.GESTOR],
    requiredModules: ['/mercado-zeh'],
    shortcuts: [{ label: 'Abrir mercado', href: '/mercado-zeh' }],
  },
  {
    id: 'configuracoes',
    title: 'Configurações',
    description: 'Empresas, usuários, kanbans, importações, templates, logs, e-mails e personalização.',
    href: '/configuracoes',
    icon: Settings,
    group: 'management',
    status: 'available',
    roles: [UserRole.ADMIN],
    requiredModules: [
      '/configuracoes',
      '/empresas',
      '/usuarios',
      '/importacao',
      '/kanban-config',
      '/log-acesso',
      '/email-personalization',
    ],
    shortcuts: [
      { label: 'Geral', href: '/configuracoes' },
      { label: 'Empresas', href: '/empresas' },
      { label: 'Usuários', href: '/usuarios' },
      { label: 'Importações', href: '/importacao' },
    ],
  },
];

const hasAllowedModules = (user: AuthUser): user is AuthUser & { allowedModules: string[] } =>
  Array.isArray(user.allowedModules) && user.allowedModules.length > 0;

export function canAccessPortalModule(user: AuthUser | null, module: PortalModule): boolean {
  if (!user || !module.roles.includes(user.role)) return false;

  if (module.status === 'soon') {
    return user.role === UserRole.ADMIN || user.role === UserRole.GESTOR;
  }

  if (!hasAllowedModules(user)) return true;

  const allowed = user.allowedModules;
  const targets = [
    module.href,
    module.openAction === 'rique' ? '/rique' : undefined,
    ...(module.requiredModules ?? []),
  ].filter((href): href is string => Boolean(href));
  return targets.some((href) => allowed.includes(href));
}

export function filterPortalShortcuts(user: AuthUser | null, module: PortalModule): PortalModuleShortcut[] {
  if (!user || !module.shortcuts) return [];

  // OPERADOR: só atalhos úteis (sem Gestor/Títulos — perfil não acessa)
  if (user.role === UserRole.OPERADOR && module.id === 'cobrancas') {
    return [
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'Kanban', href: '/kanban' },
      { label: 'Meus clientes', href: '/clientes-fornecedores' },
    ];
  }

  if (!hasAllowedModules(user)) return module.shortcuts;

  return module.shortcuts.filter((shortcut) => {
    if (shortcut.openAction === 'rique') {
      return user.allowedModules.includes('/rique');
    }
    return Boolean(shortcut.href && user.allowedModules.includes(shortcut.href));
  });
}
